import { type FunctionComponent, useState } from 'react'
import Button from '~/components/Button'
import { ConfirmationPhraseField, isPhraseTyped } from '~/components/ConfirmationPhraseField'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Modal } from '~/components/Modal'

type PureArchiveContextModalProps = {
  contextName: string
  onArchive: () => Promise<void>
}

/**
 * The confirmation for archiving a context.
 *
 * Archiving used to happen on the click, from a row in a list — the same click that could have been the
 * view icon next to it. The name has to be typed back now, for the reason this pattern exists everywhere
 * else in the inventory: typing it is what makes the viewer read which row they are on.
 *
 * A context in use cannot be archived at all, so there is no stacks warning to give here — the button is
 * already disabled on those rows, and the API refuses them too.
 */
export const PureArchiveContextModal: FunctionComponent<PureArchiveContextModalProps> = ({ contextName, onArchive }) => {
  const { close } = useGlobalModal()
  const [typedName, setTypedName] = useState('')
  const isNameConfirmed = isPhraseTyped(contextName, typedName)

  const handleArchive = async () => {
    // The disabled button is the affordance; not archiving is the invariant.
    if (!isNameConfirmed) {
      return
    }
    await onArchive()
  }

  return (
    <Modal.Content>
      <Modal.Title>Archive {contextName}</Modal.Title>
      <Modal.Body>
        <div className={'flex flex-col gap-3'}>
          <p>It will leave the context list and stop being offered to your stacks.</p>
          <p>Archived contexts stay on a separate page, but only Pubstack can bring one back.</p>
          <ConfirmationPhraseField phrase={contextName} value={typedName} onChange={setTypedName} trackingLabel={'adm-contexts-archive-type'} />
        </div>
      </Modal.Body>
      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'adm-contexts-archive-cancel'}>
          Cancel
        </Button>
        <Button variant={'negative'} iconName={'archive'} disabled={!isNameConfirmed} onClick={handleArchive} trackingLabel={'adm-contexts-archive-confirm'}>
          Archive context
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
